/**
 * Owner: Masa Bayu
 * File: ReaderProfileScreen.tsx
 * Date:Time: 2026-10-19 18:45
 * Description: §393 — skrin Profil Reader (tema QX, W3C + PWA): satu DOM
 *   untuk tiga jalur — desktop/tablet sidebar kiri (≥768), mobile cip tab
 *   wrap di bawah hero (<768). Seksyen: Ringkasan, Perpustakaan, Pesanan,
 *   Tetapan. Data 100% nyata (reader-profile-data). §417 — pemilih bahasa
 *   di sini: state `interfaceTag` drives labels + locale across all panels.
 *   §513 — SATU profil untuk reader + penerbit: `/account/<seksyen>` memilih
 *   panel supaya pautan tersimpan dan butang belakang tidak mati; lencana hero
 *   datang daripada `role` hub, bukan teks tetap.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactElement,
} from 'react';
import type { AuthSessionView } from '../auth/session-types';
import {
  getDemoLibraryRevision,
  subscribeDemoLibraryRevision,
} from '../library/demo-library-store';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import {
  getDetectedLocaleProfile,
  getMarketplaceLanguageOverride,
  isUiCatalogCertified,
  setMarketplaceLanguageOverride,
  type MarketplaceLocaleProfile,
} from '../home/marketplace/marketplace-locale';
import {
  buildReaderProfileData,
  READER_PROFILE_SECTIONS,
  sectionFromAccountPath,
  type ReaderProfileSection,
} from './reader-profile-data';
import { READER_PROFILE_LABELS } from './reader-profile-labels';
import { ReaderProfileOverview } from './ReaderProfileOverview';
import { ReaderProfileLibrary } from './ReaderProfileLibrary';
import { ReaderProfileOrders } from './ReaderProfileOrders';
import { ReaderProfilePublishing } from './ReaderProfilePublishing';
import { ReaderProfileSettings } from './ReaderProfileSettings';
import './reader-profile.css';
import './reader-profile-panels.css';

export interface ReaderProfileScreenProps {
  readonly path?: string;
  readonly authSession: AuthSessionView | null;
  readonly onNavigate: (path: string) => void;
  readonly onSignedIn: (session: AuthSessionView) => void;
  readonly onSignOut: () => void;
}

export function ReaderProfileScreen({
  path = '/account',
  authSession,
  onNavigate,
  onSignedIn,
  onSignOut,
}: ReaderProfileScreenProps): ReactElement {
  const [section, setSection] = useState<ReaderProfileSection>(() => sectionFromAccountPath(path));
  useEffect(() => {
    setSection(sectionFromAccountPath(path));
  }, [path]);
  const [reload, setReload] = useState(0);
  // §458b — KPI "Books owned" ikut geran yang tiba lewat daripada sync hub.
  const libraryRevision = useSyncExternalStore(
    subscribeDemoLibraryRevision,
    getDemoLibraryRevision,
  );
  const data = useMemo(
    () => buildReaderProfileData(authSession),
    [authSession, libraryRevision, reload],
  );
  // §417/§418 — pemilih bahasa dunia di sini: state `interfaceTag` drives
  // labels (hanya katalog tersijil) + format Intl; currency kekal ikut negara.
  const [interfaceTag, setInterfaceTag] = useState<string>(data.locale.interfaceTag);
  const locale: MarketplaceLocaleProfile = useMemo(() => {
    const certified = isUiCatalogCertified(interfaceTag);
    return {
      language: certified ? interfaceTag : 'en',
      currency: data.locale.currency,
      interfaceTag,
      uiTranslation: certified ? 'certified' : 'fallback',
    };
  }, [data.locale.currency, interfaceTag]);
  const syncInterfaceTag = useCallback((): void => {
    setInterfaceTag(getMarketplaceLanguageOverride() ?? getDetectedLocaleProfile().language);
  }, []);
  const onLanguageChange = useCallback(
    (next: string): void => {
      setMarketplaceLanguageOverride(next);
      syncInterfaceTag();
    },
    [syncInterfaceTag],
  );
  const labels = READER_PROFILE_LABELS[locale.language];
  const panel =
    section === 'overview' ? (
      <ReaderProfileOverview
        data={data}
        labels={labels}
        interfaceTag={locale.interfaceTag}
        onNavigate={onNavigate}
        onAlertsChanged={() => {
          syncInterfaceTag();
          setReload((n) => n + 1);
        }}
      />
    ) : section === 'library' ? (
      <ReaderProfileLibrary data={data} labels={labels} onNavigate={onNavigate} />
    ) : section === 'orders' ? (
      <ReaderProfileOrders
        data={data}
        labels={labels}
        interfaceTag={locale.interfaceTag}
        onNavigate={onNavigate}
      />
    ) : section === 'publishing' ? (
      <ReaderProfilePublishing
        labels={labels}
        interfaceTag={locale.interfaceTag}
        onNavigate={onNavigate}
        onSignedIn={onSignedIn}
      />
    ) : (
      <ReaderProfileSettings
        data={data}
        labels={labels}
        locale={locale}
        onLanguageChange={onLanguageChange}
        onNavigate={onNavigate}
        onSignOut={onSignOut}
      />
    );
  return (
    <main className="qxr" aria-labelledby="qxr-heading">
      <header className="qxr__hero">
        <span className="qxr__avatar" aria-hidden="true">
          {data.identity.monogram}
        </span>
        <div className="qxr__herobody">
          <h1 id="qxr-heading" className="qxr__name">
            {data.identity.displayName}
          </h1>
          {data.identity.email !== '' ? <p className="qxr__email">{data.identity.email}</p> : null}
          <span className="qxr__chip" data-role-badge={data.identity.badge}>
            <LandingIcon name={data.identity.badge === 'reader' ? 'check' : 'pen'} size={13} />{' '}
            {
              {
                reader: labels.badgeReader,
                publisher: labels.badgePublisher,
                admin: labels.badgeAdmin,
              }[data.identity.badge]
            }
          </span>
        </div>
        <button
          type="button"
          className="qxr__cta"
          data-open-author-dashboard=""
          onClick={() => onNavigate('/author/dashboard')}
        >
          <LandingIcon name="pen" size={15} />{' '}
          {locale.language === 'ms' ? 'Dashboard penulis' : 'Author Dashboard'}
        </button>
        <button
          type="button"
          className="qxr__cta qxr__cta--ghost qxr__explore"
          onClick={() => onNavigate('/all-books')}
        >
          <LandingIcon name="bag" size={15} /> {locale.language === 'ms' ? 'Jelajahi' : 'Explore'}{' '}
          <LandingIcon name="chevronRight" size={13} />
        </button>
      </header>

      <div className="qxr__body">
        <nav className="qxr__nav" aria-label={labels.ariaNav}>
          {READER_PROFILE_SECTIONS.map((id) => (
            <button
              key={id}
              type="button"
              className="qxr__navitem"
              aria-current={section === id ? 'page' : undefined}
              data-active={section === id ? 'true' : 'false'}
              onClick={() => {
                setSection(id);
                onNavigate(`/account/${id}`);
              }}
            >
              <span className="qxr__navicon" aria-hidden="true">
                <LandingIcon name={labels.sectionIcons[id]} size={20} />
              </span>
              <span>{labels.sections[id]}</span>
            </button>
          ))}
        </nav>
        <div className="qxr__panel" id="qxr-panel">
          {panel}
        </div>
      </div>
    </main>
  );
}
